import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { types as coreTypes } from '@sitebuilder/core';
import { EnvDiffApiClient } from '../api/envDiffApiClient';
import { EnvSync } from '../types';
import { ThunkResult } from './index';

export interface StateShape {
    listItems: coreTypes.EnvListItem[] | null;
    listLoading: boolean;
    listLoadError: string | null;

    sourceEnv: string | null;
    destEnv: string | null;

    diffItems: coreTypes.EnvDiff[] | null;
    diffLoading: boolean;
    diffLoadError: string | null;
    isMergeSuccessful: boolean | null;
    mergingMessage: string;
}

export const envDiffDefaultState: StateShape = {
    listItems: null,
    listLoading: false,
    listLoadError: null,

    sourceEnv: null,
    destEnv: null,

    diffItems: null,
    diffLoading: false,
    diffLoadError: null,
    isMergeSuccessful: null,
    mergingMessage: '',
};

const apiClient = new EnvDiffApiClient();

export const actionCreators = {
    listEnvironments: (): ThunkResult<Promise<coreTypes.EnvListItem[] | null>> => {
        return async (dispatch, getState) => {
            const state = getState();
            const accessToken = state.auth.user?.access_token;
            dispatch(envDiffReducerActions.LIST_LOAD_START());
            let listItems: coreTypes.EnvListItem[] | null = null;

            try {
                const items = await apiClient.list({
                    accessToken,
                });

                listItems = Array.isArray(items) ? (items as coreTypes.EnvListItem[]) : null;
                dispatch(
                    envDiffReducerActions.LIST_LOAD_SUCCESS({
                        listItems,
                    })
                );
            } catch (err: any) {
                console.error(err);
                dispatch(
                    envDiffReducerActions.LIST_LOAD_ERROR({ error: { message: err.message } })
                );
            }

            return listItems;
        };
    },

    setSourceEnv: (id: string): ThunkResult<Promise<void>> => {
        return async (dispatch) => {
            dispatch(envDiffReducerActions.SRC_SET({ id }));
        };
    },
    setDestinationEnv: (id: string): ThunkResult<Promise<void>> => {
        return async (dispatch) => {
            dispatch(envDiffReducerActions.DEST_SET({ id }));
        };
    },

    listDiff: (
        src: string | null,
        dest: string | null
    ): ThunkResult<Promise<coreTypes.EnvDiff[] | null>> => {
        return async (dispatch, getState) => {
            const state = getState();
            const accessToken = state.auth.user?.access_token;
            dispatch(envDiffReducerActions.DIFF_LOAD_START());
            try {
                if (src && dest) {
                    const items = await apiClient.getDiff(src, dest, {
                        accessToken,
                    });

                    if (items?.items) {
                        dispatch(
                            envDiffReducerActions.DIFF_LOAD_SUCCESS({ diffItems: items.items })
                        );
                        return items.items;
                    }
                }

                return null;
            } catch (err: any) {
                console.error(err);
                dispatch(
                    envDiffReducerActions.DIFF_LOAD_ERROR({ error: { message: err.message } })
                );
            }
        };
    },
    merge: (src: string, dest: string, data: EnvSync[]): ThunkResult<Promise<boolean | void>> => {
        return async (dispatch, getState) => {
            const state = getState();
            const accessToken = state.auth.user?.access_token;
            dispatch(envDiffReducerActions.MERGE_START());
            try {
                await apiClient.merge(src, dest, data, {
                    accessToken,
                });
                dispatch(envDiffReducerActions.MERGE_END(true));
                return true;
            } catch (err: any) {
                dispatch(envDiffReducerActions.MERGE_END(false));
            }
        };
    },
};

const envDiffSlice = createSlice({
    name: 'envDiff',
    initialState: envDiffDefaultState,
    reducers: {
        LIST_LOAD_START(draft) {
            draft.listLoading = true;
            draft.listLoadError = null;
            draft.mergingMessage = '';
            draft.isMergeSuccessful = null;
        },
        LIST_LOAD_SUCCESS(
            draft,
            action: PayloadAction<{ listItems: coreTypes.EnvListItem[] | null }>
        ) {
            const { listItems } = action.payload;
            draft.listLoading = false;
            draft.listItems = listItems || null;
        },
        LIST_LOAD_ERROR(draft, action: PayloadAction<{ error?: any }>) {
            const { error } = action.payload;
            draft.listLoading = false;
            draft.listLoadError = error;
        },

        SRC_SET(draft, action: PayloadAction<{ id?: string }>) {
            const { id } = action.payload;
            draft.sourceEnv = id || null;
        },

        DEST_SET(draft, action: PayloadAction<{ id?: string }>) {
            const { id } = action.payload;
            draft.destEnv = id || null;
        },

        DIFF_LOAD_START(draft) {
            draft.diffLoading = true;
            draft.mergingMessage = '';
            draft.isMergeSuccessful = null;
            // draft.diffItems = null;
        },
        DIFF_LOAD_SUCCESS(draft, action: PayloadAction<{ diffItems?: coreTypes.EnvDiff[] }>) {
            const { diffItems } = action.payload;
            draft.diffLoading = false;
            draft.diffItems = diffItems || null;
        },
        DIFF_LOAD_ERROR(draft, action: PayloadAction<{ error?: any }>) {
            const { error } = action.payload;
            draft.diffLoading = false;
            draft.diffLoadError = error;
        },
        MERGE_START(draft) {
            draft.mergingMessage = 'Merging is in progress ...';
            draft.isMergeSuccessful = null;
        },
        MERGE_END(draft, action: PayloadAction<boolean>) {
            draft.isMergeSuccessful = action.payload;
            draft.mergingMessage = action.payload ? 'Successfully merged!' : 'Merging Failed!';
        },
    },
});

const envDiffReducerActions = envDiffSlice.actions;
export const envDiffReducer = envDiffSlice.reducer;
